import * as React from 'react';
import { useParams } from 'react-router-dom';
import dashboardWrapper from '~/wrappers/dashboard';
import { Panel, Spinner } from '@ixicare/ui';
import { t } from '~/i18n';
import { State } from '~/store/types';
import connect from '~/store/connect';
import { useStores } from '~store/utils';
import ErrorContainer from '~/containers/Error';
import BackLink from '~/components/BackLink';
import HealthcareProviderFormContainer from '~/containers/HealthcareProviders/HealthcareProviderForm';

interface HealthcareProviderUpdatePageProps {
  healthcareProvider: State['healthcareProviderDetail']['profile'];
  loadHealthcareProvider: State['healthcareProviderDetail']['loadHealthcareProvider'];
  healthcareProviderLoading: State['healthcareProviderDetail']['loading'];
  healthcareProviderError: State['healthcareProviderDetail']['error'];
}

const HealthcareProviderUpdatePage: React.FC<HealthcareProviderUpdatePageProps> = ({
  healthcareProvider,
  loadHealthcareProvider,
  healthcareProviderLoading,
  healthcareProviderError,
}) => {
  const { id } = useParams<{ id: string }>();
  const healthcareProviderId = id ? parseInt(id, 10) : 0;
  const { auth } = useStores();
  const isOwnProfile = !!auth.user && auth.user.id === healthcareProviderId;

  React.useEffect(() => {
    if (healthcareProviderLoading) {
      return;
    }
    if (healthcareProviderId && (!healthcareProvider || healthcareProvider.id !== healthcareProviderId)) {
      loadHealthcareProvider(healthcareProviderId);
    }
  }, [healthcareProviderId, healthcareProvider, healthcareProviderLoading, loadHealthcareProvider]);

  React.useEffect(() => {
    document.title = t('meta.title', {
      title: isOwnProfile ? t('healthcareProviders.editMyAccount.title') : t('healthcareProviders.update.title'),
    });
  }, [isOwnProfile]);

  if (healthcareProviderLoading) {
    return <Spinner label={t('global.loading')} />;
  }
  return (
    <>
      <h1>{isOwnProfile ? t('healthcareProviders.editMyAccount.title') : t('healthcareProviders.update.title')}</h1>
      {!isOwnProfile && (
        <BackLink fallbackUrl={`/healthcare-providers`} name={t('healthcareProviderDetail.goBackToList')} />
      )}
      <ErrorContainer error={healthcareProviderError} />
      {!healthcareProviderError && (
        <Panel>
          <HealthcareProviderFormContainer healthcareProvider={healthcareProvider} />
        </Panel>
      )}
    </>
  );
};

const ConnectedHealthcareProviderUpdatePage = connect((state: State) => ({
  healthcareProvider: state.healthcareProviderDetail.profile,
  loadHealthcareProvider: state.healthcareProviderDetail.loadHealthcareProvider,
  healthcareProviderLoading: state.healthcareProviderDetail.loading,
  healthcareProviderError: state.healthcareProviderDetail.error,
}))(HealthcareProviderUpdatePage);

export default dashboardWrapper(ConnectedHealthcareProviderUpdatePage);
